import React, { useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import { alpha, styled } from '@mui/material/styles';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import DragDropPlugin from '@uppy/drag-drop';
import type { Uppy } from '@uppy/core';
import generateNoteString from '../utils/generate-note-string';
import { type MediaType, MEDIA_TYPE_PLURAL } from '../editor/fields/media-type-config';

/**
 * Props for the MediaDropzone component.
 */
export interface MediaDropzoneProps {
  /**
   * The Uppy instance to attach the DragDrop plugin to.
   * The component does not manage Uppy lifecycle - consumers must provide their own instance.
   */
  uppy: Uppy<Record<string, unknown>, Record<string, unknown>>;

  /**
   * Optional height override as a spacing multiplier.
   * Default is 9 (theme.spacing(9) = 72px).
   * Example: height={19} → theme.spacing(19) = 152px
   */
  height?: number;

  /**
   * Whether the dropzone is disabled.
   * When disabled, the component renders with reduced opacity and does not attach the DragDrop plugin.
   */
  disabled?: boolean;

  /**
   * Media type for upload text.
   * When provided, derives the upload type text (e.g., 'photo' → 'photos').
   * If not provided, renders: "Click to upload or drag and drop"
   */
  mediaType?: MediaType;

  /**
   * Allowed file types for the restriction note.
   * Example: ['.png', '.jpg']
   */
  allowedFileTypes?: string[] | null;

  /**
   * Optional ref callback or RefObject to receive the dropzone HTMLElement.
   * Useful for advanced plugin configuration or event handling.
   */
  dropzoneRef?: React.RefCallback<HTMLElement> | React.RefObject<HTMLElement>;
}

interface StyledContainerProps {
  height?: number;
  disabled?: boolean;
}

const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'height' && prop !== 'disabled',
})<StyledContainerProps>(({ theme, height, disabled }) => ({
  position: 'relative',
  width: '100%',
  height: theme.spacing(height ?? 9),
  borderRadius: theme.spacing(0.5),
  backgroundColor: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  opacity: disabled ? 0.5 : 1,
  pointerEvents: disabled ? 'none' : 'auto',
  transition: 'opacity 0.2s ease-in-out',

  '& .uppy-DragDrop-container': {
    backgroundColor: alpha(theme.palette.common.black, 0.06),
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    transition: 'background-color 0.2s ease-in-out',

    '&:hover': {
      backgroundColor: disabled ? alpha(theme.palette.common.black, 0.06) : alpha(theme.palette.common.black, 0.09),
    },
  },

  '& .uppy-DragDrop-inner': {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    marginLeft: theme.spacing(8),
  },

  '& .uppy-DragDrop-label': {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    fontStyle: 'normal',
    fontWeight: theme.typography.body2.fontWeight,
    lineHeight: theme.typography.body2.lineHeight,
    color: theme.palette.text.primary,
    textAlign: 'left',
  },

  '& .uppy-DragDrop-note': {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.caption.fontSize,
    fontStyle: 'normal',
    fontWeight: theme.typography.caption.fontWeight,
    lineHeight: theme.typography.caption.lineHeight,
    color: theme.palette.text.secondary,
    textAlign: 'left',
  },

  '& .uppy-DragDrop-browse': {
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    textDecoration: 'underline',
    cursor: disabled ? 'default' : 'pointer',

    '&:hover, &:active': {
      color: disabled ? theme.palette.text.primary : theme.palette.text.secondary,
    },
  },

  '& .uppy-DragDrop--isDragDropSupported': {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.spacing(0.5),
  },

  '& .uppy-DragDrop--isDraggingOver': {
    backgroundColor: disabled ? alpha(theme.palette.common.black, 0.06) : alpha(theme.palette.common.black, 0.09),
  },

  '& .uppy-Root': {
    width: '100%',
    height: '100%',
  },

  '& .uppy-DragDrop-arrow': {
    display: 'none',
  },
}));

const StyledIcon = styled(UploadFileOutlinedIcon)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: theme.spacing(3),
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  width: theme.spacing(3),
  height: theme.spacing(3),
  color: theme.palette.text.primary,
  zIndex: 1,
}));

const MediaDropzone: React.FC<MediaDropzoneProps> = ({
  uppy,
  height,
  disabled = false,
  mediaType,
  allowedFileTypes = null,
  dropzoneRef,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const attachedPluginRef = useRef<boolean>(false);

  // Handle dropzoneRef (both callback ref and RefObject)
  const setRefs = useCallback(
    (element: HTMLDivElement | null) => {
      containerRef.current = element;

      // Call external ref if provided
      if (dropzoneRef) {
        if (typeof dropzoneRef === 'function') {
          dropzoneRef(element);
        } else {
          const refObject = dropzoneRef as React.MutableRefObject<HTMLElement | null>;
          refObject.current = element;
        }
      }
    },
    [dropzoneRef],
  );

  // Attach/detach Uppy DragDrop plugin
  useEffect(() => {
    // Skip attachment if disabled
    if (disabled) {
      attachedPluginRef.current = false;
      return undefined;
    }

    const target = containerRef.current;
    if (!target || !uppy) {
      return undefined;
    }

    // Check if Uppy instance already has a DragDrop plugin
    const existingPlugin = uppy.getPlugin('DragDrop');
    if (existingPlugin) {
      console.error(
        'MediaDropzone: Uppy instance already has DragDrop plugin attached. Cannot attach another.',
      );
      attachedPluginRef.current = false;
      return undefined;
    }

    // Generate the note string for file restrictions (without file size per product requirements)
    const noteString = generateNoteString(allowedFileTypes);

    // Build browse text based on mediaType
    const browseText = mediaType
      ? `Click to upload ${MEDIA_TYPE_PLURAL[mediaType].toLowerCase()}`
      : 'Click to upload';

    // Attach the DragDrop plugin
    try {
      uppy.use(DragDropPlugin, {
        target,
        note: noteString,
        locale: {
          strings: {
            browse: browseText,
            dropHereOr: '%{browse} or drag and drop',
          },
        },
      });
      attachedPluginRef.current = true;
    } catch (error) {
      console.error('MediaDropzone: Failed to attach DragDrop plugin:', error);
      attachedPluginRef.current = false;
    }

    // Cleanup: remove plugin on unmount (only if this component attached it)
    return () => {
      if (attachedPluginRef.current) {
        try {
          const plugin = uppy.getPlugin('DragDrop');
          if (plugin) {
            uppy.removePlugin(plugin);
          }
        } catch (error) {
          // Uppy instance may have been destroyed before unmount
          console.error('MediaDropzone: Failed to remove DragDrop plugin on unmount:', error);
        }
        attachedPluginRef.current = false;
      }
    };
  }, [uppy, disabled, mediaType, allowedFileTypes]);

  return (
    <StyledContainer ref={setRefs} height={height} disabled={disabled}>
      <StyledIcon data-testid="media-dropzone-upload-icon" />
    </StyledContainer>
  );
};

export default MediaDropzone;
